"use client"
import { FileText, Download, Calendar, User } from "lucide-react"

export default function Forms() {
  const forms = [
    {
      name: "Installation Request Form",
      description: "Request new installation services",
      type: "PDF",
      size: "245 KB",
      lastUpdated: "2024-01-15",
      category: "Installation",
    },
    {
      name: "Service Agreement",
      description: "Standard service terms and conditions",
      type: "PDF",
      size: "189 KB",
      lastUpdated: "2024-01-10",
      category: "Legal",
    },
    {
      name: "Warranty Information",
      description: "Product warranty details and coverage",
      type: "PDF",
      size: "156 KB",
      lastUpdated: "2024-01-08",
      category: "Warranty",
    },
    {
      name: "Customer Feedback Form",
      description: "Service quality feedback and suggestions",
      type: "PDF",
      size: "98 KB",
      lastUpdated: "2024-01-12",
      category: "Feedback",
    },
    {
      name: "Emergency Contact Form",
      description: "Emergency contact information update",
      type: "PDF",
      size: "112 KB",
      lastUpdated: "2024-01-14",
      category: "Contact",
    },
    {
      name: "Change Order Request",
      description: "Request modifications to existing orders",
      type: "PDF",
      size: "203 KB",
      lastUpdated: "2024-01-11",
      category: "Orders",
    },
  ]

  const categories = ["All", "Installation", "Legal", "Warranty", "Feedback", "Contact", "Orders"]

  const getCategoryColor = (category: string) => {
    const colors = {
      Installation: "bg-blue-100 text-blue-800",
      Legal: "bg-red-100 text-red-800",
      Warranty: "bg-green-100 text-green-800",
      Feedback: "bg-yellow-100 text-yellow-800",
      Contact: "bg-purple-100 text-purple-800",
      Orders: "bg-indigo-100 text-indigo-800",
    }
    return colors[category as keyof typeof colors] || "bg-gray-100 text-gray-800"
  }

  return (
    <div className="p-4 lg:p-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl lg:text-3xl font-bold text-[#004261] mb-2 uppercase tracking-wide">
            Forms & Documents
          </h1>
          <div className="w-16 h-1 bg-[#00CCFE] mb-4"></div>
          <p className="text-gray-600">Download important forms and documentation</p>
        </div>

        {/* Category Filter */}
        <div className="mb-6">
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                className="px-4 py-2 text-sm font-medium rounded-lg border border-[#00CCFE] text-[#004261] hover:bg-[#00CCFE] hover:text-white transition-colors"
              >
                {category}
              </button>
            ))}
          </div>
        </div>

        {/* Forms Grid */}
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {forms.map((form, index) => (
            <div
              key={index}
              className="bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-shadow"
            >
              {/* Header */}
              <div className="bg-[#004261] px-6 py-4">
                <div className="flex items-center">
                  <FileText className="h-6 w-6 text-white mr-3" />
                  <div className="flex-1">
                    <h3 className="text-white font-bold text-sm uppercase">{form.name}</h3>
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mt-2 ${getCategoryColor(form.category)}`}
                    >
                      {form.category}
                    </span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <p className="text-gray-600 text-sm mb-4">{form.description}</p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center text-sm text-gray-500">
                    <FileText className="h-4 w-4 text-[#00CCFE] mr-2" />
                    <span>Type: {form.type}</span>
                    <span className="mx-2">•</span>
                    <span>Size: {form.size}</span>
                  </div>

                  <div className="flex items-center text-sm text-gray-500">
                    <Calendar className="h-4 w-4 text-[#00CCFE] mr-2" />
                    <span>Updated: {form.lastUpdated}</span>
                  </div>
                </div>

                {/* Download Button */}
                <button className="w-full bg-[#00CCFE] text-white py-2 px-4 rounded font-medium hover:bg-[#004261] transition-colors flex items-center justify-center">
                  <Download className="h-4 w-4 mr-2" />
                  Download {form.type}
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Help Section */}
        <div className="mt-8 bg-blue-50 border border-[#00CCFE] rounded-lg p-6">
          <div className="flex items-start">
            <User className="h-5 w-5 text-[#00CCFE] mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-bold text-[#004261] mb-2 uppercase">Need Help?</h3>
              <p className="text-sm text-gray-600 mb-4">
                If you need assistance completing any forms or have questions about required documentation, please
                contact our support team.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href="tel:(800) 555-0100"
                  className="inline-flex items-center px-4 py-2 bg-[#004261] text-white rounded font-medium hover:bg-[#00283b] transition-colors"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Call Support
                </a>
                <a
                  href="mailto:support@company.com"
                  className="inline-flex items-center px-4 py-2 bg-[#00CCFE] text-white rounded font-medium hover:bg-[#004261] transition-colors"
                >
                  <User className="h-4 w-4 mr-2" />
                  Email Support
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
